import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type EmergencyCreditStatsCommon } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { CancellationCreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CancellationCreditProgressBar";
import {
  GOLDEN_RATIO_BOTTOM_FLEX,
  GOLDEN_RATIO_TOP_FLEX,
} from "@src/appV2/redesign/utils/goldenRatioLayout";

import { CancellationScoreDisplay } from "./components/CancellationScoreDisplay";
import { RestrictedBadge } from "./components/RestrictedBadge";
import { PENALTY_SCORE_ANIMATION_DURATION_MS } from "./hooks/useCancellationScoreAnimation";

export interface ShiftCancelPageConfirmedData {
  finalScore: number;
  isWithinECWindow: boolean;
  finalECStats?: EmergencyCreditStatsCommon;
  isBecomingRestricted: boolean;
  isAlreadyRestricted: boolean;
  isFinalRestriction: boolean;
  reEnrollmentScore: number | undefined;
}

export interface ShiftCancelPageConfirmedHandlers {
  onContinue: () => void;
  isLoading?: boolean;
}

type ShiftCancelPageConfirmationScreenProps = Omit<
  ShiftCancelPageConfirmedData,
  "isFinalRestriction" | "reEnrollmentScore"
> &
  ShiftCancelPageConfirmedHandlers;

export function ShiftCancelPageConfirmationScreen(
  props: ShiftCancelPageConfirmationScreenProps
): JSX.Element {
  const {
    finalScore,
    isWithinECWindow,
    finalECStats,
    isBecomingRestricted,
    isAlreadyRestricted,
    isLoading = false,
    onContinue,
  } = props;

  const isRestricted = isBecomingRestricted || isAlreadyRestricted;

  const showCreditProgress =
    !isRestricted &&
    isWithinECWindow &&
    isDefined(finalECStats) &&
    finalECStats.totalAvailableCredits === 0;

  return (
    <Stack textAlign="center" flex={1}>
      <Box sx={{ flex: GOLDEN_RATIO_TOP_FLEX, minHeight: 0 }} />

      <Stack spacing={12} sx={{ paddingX: 4 }}>
        <CancellationScoreDisplay
          animated
          score={finalScore}
          duration={PENALTY_SCORE_ANIMATION_DURATION_MS}
          isRestricted={isRestricted}
          isBecomingRestricted={isBecomingRestricted}
        >
          {isRestricted ? (
            <Stack alignItems="center" sx={{ marginTop: "56px !important" }}>
              <RestrictedBadge />
            </Stack>
          ) : null}
          {showCreditProgress && (
            <CancellationCreditProgressBar cancellationCreditStats={finalECStats} />
          )}
        </CancellationScoreDisplay>
      </Stack>

      <Box sx={{ flex: GOLDEN_RATIO_BOTTOM_FLEX, minHeight: 0 }} />

      <DialogFooter orientation="vertical">
        <LoadingButton
          fullWidth
          isLoading={isLoading}
          variant="contained"
          size="large"
          onClick={onContinue}
        >
          {isBecomingRestricted ? "Next" : "Got it"}
        </LoadingButton>
      </DialogFooter>
    </Stack>
  );
}
